export const HEIST_SID_COOKIE_NAME = 'heist_sid';
export const HEIST_EMAIL_COOKIE_NAME = 'heist_email';

const HISTORY_SID_LOOKUP_ROW_LIMIT = 80;
const SNAPSHOT_HEIST_SID_DENYLIST = new Set(['s_ms2w6s39_8ktbrpf4']);

const HISTORY_SID_PREFERRED_PATHS = [
  '/api/customer/1001',
  '/api/reset',
  '/api/token',
] as const;

const CURRENT_SESSION_PATH_PREFIXES = ['/api/reset'] as const;

const CURRENT_SESSION_SCORE_BOOST = 10_000_000_000_000;
const REGISTERED_EMAIL_SCORE_BOOST = 1_000_000_000_000;
const PREFERRED_PATH_SCORE_BOOST = 100_000_000_000;

export function readHeistSidFromCookieHeader(
  cookieHeader: string | undefined,
): string | null {
  if (!cookieHeader) {
    return null;
  }

  for (const part of cookieHeader.split(';')) {
    const trimmed = part.trim();
    if (!trimmed) {
      continue;
    }
    const separatorIndex = trimmed.indexOf('=');
    if (separatorIndex <= 0) {
      continue;
    }
    const name = trimmed.slice(0, separatorIndex).trim();
    if (name !== HEIST_SID_COOKIE_NAME) {
      continue;
    }
    const value = trimmed.slice(separatorIndex + 1).trim();
    return value || null;
  }

  return null;
}

export function cookieHeaderHasName({
  cookieHeader,
  cookieName,
}: {
  cookieHeader: string | undefined;
  cookieName: string;
}): boolean {
  if (!cookieHeader) {
    return false;
  }

  return cookieHeader.split(';').some((part) => {
    const trimmed = part.trim();
    const separatorIndex = trimmed.indexOf('=');
    if (separatorIndex <= 0) {
      return false;
    }
    return trimmed.slice(0, separatorIndex).trim() === cookieName;
  });
}

export function findCookieHeaderKey(
  headers: Record<string, string>,
): string | undefined {
  return Object.keys(headers).find((key) => key.toLowerCase() === 'cookie');
}

export function readHeistSidFromRequestHeaders(
  headers: Record<string, string>,
): string | null {
  const cookieKey = findCookieHeaderKey(headers);
  return readHeistSidFromCookieHeader(
    cookieKey ? headers[cookieKey] : undefined,
  );
}

export function mergeHeistSidIntoHeaders({
  headers,
  heistSid,
}: {
  headers: Record<string, string>;
  heistSid: string;
}): Record<string, string> {
  const existing = readHeistSidFromRequestHeaders(headers);
  if (existing) {
    return headers;
  }

  const cookieKey = findCookieHeaderKey(headers) ?? 'Cookie';
  const current = headers[cookieKey]?.trim();
  const heistPair = `${HEIST_SID_COOKIE_NAME}=${heistSid}`;
  const nextCookie = current ? `${current}; ${heistPair}` : heistPair;

  return {
    ...headers,
    [cookieKey]: nextCookie,
  };
}

export function replaceHeistSidInHeaders({
  headers,
  heistSid,
}: {
  headers: Record<string, string>;
  heistSid: string;
}): Record<string, string> {
  const cookieKey = findCookieHeaderKey(headers) ?? 'Cookie';
  const current = headers[cookieKey]?.trim();
  if (!current) {
    return mergeHeistSidIntoHeaders({ headers, heistSid });
  }

  const heistPair = `${HEIST_SID_COOKIE_NAME}=${heistSid}`;
  let replaced = false;
  const nextParts = current
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const separatorIndex = part.indexOf('=');
      if (separatorIndex <= 0) {
        return part;
      }
      const name = part.slice(0, separatorIndex).trim();
      if (name !== HEIST_SID_COOKIE_NAME) {
        return part;
      }
      replaced = true;
      return heistPair;
    });

  if (!replaced) {
    nextParts.push(heistPair);
  }

  return {
    ...headers,
    [cookieKey]: nextParts.join('; '),
  };
}

export function parseHistoryRequestHeadersJson(
  requestHeaders: string | null,
): Record<string, string> {
  if (!requestHeaders) {
    return {};
  }

  try {
    const parsed = JSON.parse(requestHeaders) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }

    const headers: Record<string, string> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'string') {
        headers[key] = value;
        continue;
      }
      if (Array.isArray(value)) {
        headers[key] = value.map(String).join(', ');
      }
    }
    return headers;
  } catch {
    return {};
  }
}

export function extractHeistSidFromHistoryRequestHeadersJson(
  requestHeaders: string | null,
): string | null {
  return readHeistSidFromRequestHeaders(
    parseHistoryRequestHeadersJson(requestHeaders),
  );
}

export function hostFromRequestUrl(url: string): string | null {
  try {
    return new URL(url).host || null;
  } catch {
    return null;
  }
}

export function pathFromRequestUrl(url: string): string | null {
  try {
    return new URL(url).pathname || null;
  } catch {
    return null;
  }
}

export function normalizeApiPath(path: string): string {
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path;
}

export function isVaultGameApiPath(path: string | null): boolean {
  if (!path) {
    return false;
  }

  const normalized = normalizeApiPath(path);
  if (
    normalized === '/api/maintenance' ||
    normalized === '/api/token' ||
    normalized === '/api/reset' ||
    normalized === '/api/admin/vault-notes'
  ) {
    return true;
  }

  return (
    normalized.startsWith('/api/customer/') ||
    normalized.startsWith('/api/vault/')
  );
}

export function isVaultGameApiUrl(url: string): boolean {
  return isVaultGameApiPath(pathFromRequestUrl(url));
}

export function isCurrentSessionPath(path: string | null): boolean {
  if (!path) {
    return false;
  }
  const normalized = normalizeApiPath(path);
  return CURRENT_SESSION_PATH_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`),
  );
}

export type HistoryHeistSidCandidate = {
  heistSid: string;
  path: string | null;
  hasHeistEmail: boolean;
  timestamp: number;
};

export function scoreHistoryHeistSidCandidate(
  candidate: HistoryHeistSidCandidate,
): number {
  let score = candidate.timestamp;
  if (isCurrentSessionPath(candidate.path)) {
    score += CURRENT_SESSION_SCORE_BOOST;
  }
  if (candidate.hasHeistEmail) {
    score += REGISTERED_EMAIL_SCORE_BOOST;
  }
  if (
    candidate.path &&
    HISTORY_SID_PREFERRED_PATHS.some((preferred) =>
      candidate.path?.startsWith(preferred),
    )
  ) {
    score += PREFERRED_PATH_SCORE_BOOST;
  }
  return score;
}

export function pickBestHistoryHeistSid(
  candidates: HistoryHeistSidCandidate[],
): string | null {
  const usable = candidates.filter(
    (candidate) => !SNAPSHOT_HEIST_SID_DENYLIST.has(candidate.heistSid),
  );
  if (usable.length === 0) {
    return null;
  }

  return usable.reduce((best, current) =>
    scoreHistoryHeistSidCandidate(current) > scoreHistoryHeistSidCandidate(best)
      ? current
      : best,
  ).heistSid;
}

export function buildHistoryHeistSidCandidate({
  requestHeaders,
  url,
  path,
  timestamp,
}: {
  requestHeaders: string | null;
  url?: string | null;
  path?: string | null;
  timestamp?: number | null;
}): HistoryHeistSidCandidate | null {
  const headers = parseHistoryRequestHeadersJson(requestHeaders);
  const heistSid = readHeistSidFromRequestHeaders(headers);
  if (!heistSid) {
    return null;
  }

  const cookieKey = findCookieHeaderKey(headers);
  const cookieHeader = cookieKey ? headers[cookieKey] : undefined;
  const resolvedPath = path ?? (url ? pathFromRequestUrl(url) : null) ?? null;

  return {
    heistSid,
    path: resolvedPath,
    hasHeistEmail: cookieHeaderHasName({
      cookieHeader,
      cookieName: HEIST_EMAIL_COOKIE_NAME,
    }),
    timestamp: typeof timestamp === 'number' ? timestamp : 0,
  };
}

export async function resolveLatestHeistSidFromHistory({
  targetId,
  targetType,
  host,
}: {
  targetId: string;
  targetType: string;
  host: string;
}): Promise<string | null> {
  if (!window.electronAPI?.history?.getRows) {
    return null;
  }

  const result = await window.electronAPI.history.getRows({
    targetId,
    targetType,
    startRow: 0,
    endRow: HISTORY_SID_LOOKUP_ROW_LIMIT,
    sortModel: [{ colId: 'timestamp', sort: 'desc' }],
    filters: { host },
  });

  if (!result.success || !result.data?.rows?.length) {
    return null;
  }

  const candidates = result.data.rows
    .map((row) =>
      buildHistoryHeistSidCandidate({
        requestHeaders: row.request_headers,
        url: row.url,
        path: row.path,
        timestamp: row.timestamp,
      }),
    )
    .filter((candidate): candidate is HistoryHeistSidCandidate =>
      Boolean(candidate),
    );

  return pickBestHistoryHeistSid(candidates);
}

export async function ensureHeistSidCookieOnReplayHeaders({
  headers,
  url,
  targetId,
  targetType,
}: {
  headers: Record<string, string>;
  url: string;
  targetId: string;
  targetType: string;
}): Promise<Record<string, string>> {
  if (!isVaultGameApiUrl(url)) {
    return headers;
  }

  const host = hostFromRequestUrl(url);
  if (!host) {
    return headers;
  }

  const existing = readHeistSidFromRequestHeaders(headers);
  const heistSid = await resolveLatestHeistSidFromHistory({
    targetId,
    targetType,
    host,
  });
  if (!heistSid) {
    return headers;
  }

  if (existing === heistSid) {
    return headers;
  }

  if (!existing) {
    return mergeHeistSidIntoHeaders({ headers, heistSid });
  }

  return replaceHeistSidInHeaders({ headers, heistSid });
}
